import { notifyAdmin } from "./adminNotifications";

/**
 * Integrates admin notification for guest pass creation
 * @param {Object} guestPassData - Guest pass data
 * @param {Object} currentUser - Current user object from Redux
 * @returns {Promise<void>}
 */
export const notifyGuestPassCreation = async (guestPassData, currentUser) => {
  const unitNumbers = currentUser.linkedIdentifiers?.join(", ") || "Unknown";

  await notifyAdmin(
    "guestPass",
    currentUser.displayName,
    unitNumbers,
    guestPassData.id || guestPassData._id,
    {
      guestCount: guestPassData.guests?.length || 1,
      visitDate: guestPassData.date,
      visitTime: guestPassData.time,
    }
  );
};

/**
 * Integrates admin notification for gate pass creation
 * @param {Object} gatePassData - Gate pass data
 * @param {Object} currentUser - Current user object from Redux
 * @param {string} passId - ID of the created gate pass
 * @returns {Promise<void>}
 */
export const notifyGatePassCreation = async (
  gatePassData,
  currentUser,
  passId
) => {
  await notifyAdmin(
    "gatePass",
    currentUser.displayName,
    gatePassData.identifier || currentUser.linkedIdentifiers?.[0] || "Unknown",
    passId,
    {
      deliveryDate: gatePassData.deliveryDate,
      deliveryTime: gatePassData.deliveryTime,
      deliveryCompany: gatePassData.deliveryCompany,
    }
  );
};

/**
 * Integrates admin notification for work permit creation
 * @param {Object} workPermitData - Work permit data
 * @param {Object} currentUser - Current user object from Redux
 * @param {string} workPermitId - ID of the created work permit
 * @returns {Promise<void>}
 */
export const notifyWorkPermitCreation = async (
  workPermitData,
  currentUser,
  workPermitId
) => {
  await notifyAdmin(
    "workPermit",
    currentUser.displayName,
    workPermitData.identifier ||
      currentUser.linkedIdentifiers?.[0] ||
      "Unknown",
    workPermitId,
    {
      workType: workPermitData.workPermitType,
      company: workPermitData.companyName,
      startDate: workPermitData.startDate,
      estimatedDuration: workPermitData.duration,
    }
  );
};

/**
 * Integrates admin notification for facility booking
 * @param {Object} bookingData - Facility booking data
 * @param {Object} currentUser - Current user object from Redux
 * @param {string} bookingId - ID of the created booking
 * @returns {Promise<void>}
 */
export const notifyFacilityBooking = async (
  bookingData,
  currentUser,
  bookingId
) => {
  await notifyAdmin(
    "facilityBooking",
    currentUser.displayName,
    currentUser.linkedIdentifiers?.[0] || "Unknown",
    bookingId,
    {
      facilityName: bookingData.facilityName,
      bookingDate: bookingData.date,
      bookingTime: bookingData.time,
      numberOfGuests: bookingData.numberOfGuests,
    }
  );
};

/**
 * Integrates admin notification for bill payment
 * @param {Object} paymentData - Payment data
 * @param {Object} currentUser - Current user object from Redux
 * @param {string} paymentId - ID of the payment
 * @returns {Promise<void>}
 */
export const notifyBillPayment = async (
  paymentData,
  currentUser,
  paymentId
) => {
  await notifyAdmin(
    "payment",
    currentUser.displayName,
    currentUser.linkedIdentifiers?.[0] || "Unknown",
    paymentId,
    {
      amount: paymentData.amount,
      method: paymentData.paymentMethod,
      billType: paymentData.billType,
      reference: paymentData.reference,
    }
  );
};

/**
 * Integrates admin notification for concern filing
 * @param {Object} concernData - Concern data
 * @param {Object} currentUser - Current user object from Redux
 * @param {string} concernId - ID of the created concern
 * @returns {Promise<void>}
 */
export const notifyConcernFiling = async (
  concernData,
  currentUser,
  concernId
) => {
  await notifyAdmin(
    "concern",
    currentUser.displayName,
    currentUser.linkedIdentifiers?.[0] || "Unknown",
    concernId,
    {
      concernType: concernData.concernType,
      priority: concernData.priority,
      title: concernData.title,
    }
  );
};

/**
 * Integrates admin notification for maintenance request
 * @param {Object} requestData - Maintenance request data
 * @param {Object} currentUser - Current user object from Redux
 * @param {string} requestId - ID of the created request
 * @returns {Promise<void>}
 */
export const notifyMaintenanceRequest = async (
  requestData,
  currentUser,
  requestId
) => {
  await notifyAdmin(
    "maintenanceRequest",
    currentUser.displayName,
    currentUser.linkedIdentifiers?.[0] || "Unknown",
    requestId,
    {
      requestType: requestData.requestType,
      priority: requestData.priority,
      description: requestData.description?.substring(0, 50),
    }
  );
};

/**
 * Integrates admin notification for car sticker application
 * @param {Object} stickerData - Car sticker application data
 * @param {Object} currentUser - Current user object from Redux
 * @param {string} applicationId - ID of the created application
 * @returns {Promise<void>}
 */
export const notifyCarStickerApplication = async (
  stickerData,
  currentUser,
  applicationId
) => {
  await notifyAdmin(
    "carSticker",
    currentUser.displayName,
    currentUser.linkedIdentifiers?.[0] || "Unknown",
    applicationId,
    {
      vehicleMake: stickerData.vehicleMake,
      vehicleModel: stickerData.vehicleModel,
      plateNumber: stickerData.plateNumber,
    }
  );
};
